import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import VoiceLibrary from './VoiceLibrary';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Field,
  Input,
  Loading,
  Select,
  Toggle,
} from '@/components/ui';

/**
 * Speech and voice, per client. Admin only.
 *
 * The model used to be a property of the provider row and the voice a property
 * of the bot. That works for one client on one vendor and stops working the
 * moment two clients want different voices on the same provider, or one wants
 * Nova-3 while another stays on Nova-2 — the provider instance is built once
 * and shared.
 *
 * This is deliberately not in the client portal. A client choosing their own
 * speech model is choosing their own bill and their own quality floor, without
 * being shown either.
 *
 * Everything offered here comes from `/admin/ai-settings/catalogue`, so adding
 * a model is one line in `app/ai_settings.py` and nothing in this file changes.
 */

interface Provider {
  id: string;
  kind: string;
  key: string;
  name: string;
  driver: string;
  is_default: boolean;
}

interface ModelOption {
  id: string;
  name: string;
  note?: string;
}

interface SettingField {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  hint?: string;
}

interface Catalogue {
  providers: Provider[];
  stt_models: Record<string, ModelOption[]>;
  tts_models: Record<string, ModelOption[]>;
  llm_models: Record<string, ModelOption[]>;
  voice_settings: Record<string, SettingField[]>;
}

interface Settings {
  stt: { provider_id: string | null; model: string; inherited: boolean };
  tts: {
    provider_id: string | null;
    model: string;
    voice_id: string;
    settings: Record<string, number>;
    inherited: boolean;
  };
  llm: { provider_id: string | null; model: string; inherited: boolean };
  voice_cache: { enabled: boolean };
  effective: {
    stt_model: string;
    tts_model: string;
    tts_voice_id: string;
    llm_model: string;
    source: Record<string, string>;
  };
  /** Which vendor each service will actually use, resolved as a call does. */
  providers_in_use?: Array<{
    service: string;
    provider: string;
    driver: string;
    chosen_by: string;
  }>;
  cache?: { entries: number; bytes: number; hits: number };
}

function bytes(value: number | undefined): string {
  if (!value) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = value;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function ClientAiSettings({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [clearing, setClearing] = useState(false);

  const catalogue = useQuery({
    queryKey: ['ai-catalogue'],
    queryFn: () => api.get<Catalogue>('/admin/ai-settings/catalogue'),
    staleTime: Infinity,
  });

  const settings = useQuery({
    queryKey: ['client-ai-settings', tenantId],
    queryFn: () => api.get<Settings>(`/admin/clients/${tenantId}/ai-settings`),
  });

  // Seeded once from the server so the form shows what is stored, then left
  // alone — re-seeding on every refetch would throw away what somebody is
  // halfway through typing.
  useEffect(() => {
    if (!settings.data) return;
    setDraft({
      stt_provider_id: settings.data.stt.provider_id ?? '',
      stt_model: settings.data.stt.model,
      tts_provider_id: settings.data.tts.provider_id ?? '',
      tts_model: settings.data.tts.model,
      tts_voice_id: settings.data.tts.voice_id,
      tts_settings: settings.data.tts.settings ?? {},
      llm_provider_id: settings.data.llm?.provider_id ?? '',
      llm_model: settings.data.llm?.model ?? '',
      voice_cache_enabled: settings.data.voice_cache.enabled,
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, any>) =>
      api.put(`/admin/clients/${tenantId}/ai-settings`, body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['client-ai-settings', tenantId] }),
  });

  const clearCache = useMutation({
    mutationFn: () => api.delete(`/admin/tts-cache?tenant_id=${tenantId}`),
    onSuccess: () => {
      setClearing(false);
      queryClient.invalidateQueries({ queryKey: ['client-ai-settings', tenantId] });
    },
  });

  if (catalogue.isPending || settings.isPending) return <Loading />;

  const providers = catalogue.data?.providers ?? [];
  const sttProviders = providers.filter((p) => p.kind === 'STT');
  const ttsProviders = providers.filter((p) => p.kind === 'TTS');
  const llmProviders = providers.filter((p) => p.kind === 'LLM');

  const driverOf = (id: string) =>
    providers.find((p) => p.id === id)?.driver ??
    (id === '' ? (providers.find((p) => p.is_default)?.driver ?? '') : '');

  const sttDriver = driverOf(draft.stt_provider_id ?? '') || 'deepgram';
  const ttsDriver = driverOf(draft.tts_provider_id ?? '') || 'elevenlabs';
  const llmDriver = driverOf(draft.llm_provider_id ?? '') || 'openai';

  const sttModels = catalogue.data?.stt_models[sttDriver] ?? [];
  const ttsModels = catalogue.data?.tts_models[ttsDriver] ?? [];
  const llmModels = catalogue.data?.llm_models[llmDriver] ?? [];
  const voiceFields = catalogue.data?.voice_settings[ttsDriver] ?? [];

  const set = (patch: Record<string, any>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <div className="space-y-4">
      <p className="rounded-md bg-sunken p-3 text-sm text-ink-700 dark:text-ink-200">
        These settings apply to <strong>all AI services belonging to this client</strong>{' '}
        — AI Bots, Soundboard Bots and anything added later. A bot that has been
        given its own voice keeps it; everything else inherits from here.
        <br />
        <span className="text-xs text-ink-500">
          Admin only. The client cannot see or change any of this from their own
          portal.
        </span>
      </p>

      {/* WHICH VENDOR each service will actually use.
          The sections below set models; this says who serves the call. An
          operator who set ElevenLabs and is hearing Deepgram is asking about
          the provider, and this page could not answer that at all — it showed
          the model they chose while a different vendor served every call. */}
      {settings.data?.providers_in_use?.length ? (
        <Card
          title="What a call will use"
          description="Resolved the way a live call resolves it. The account's choice wins over the bot's."
          className="mb-4"
        >
          <div className="grid gap-2 sm:grid-cols-3">
            {settings.data.providers_in_use.map((row) => (
              <div
                key={row.service}
                className="rounded-md border border-line px-3 py-2"
              >
                <div className="text-2xs uppercase tracking-wide text-ink-500">
                  {row.service === 'stt'
                    ? 'Speech to text'
                    : row.service === 'tts'
                      ? 'Text to speech'
                      : 'Language model'}
                </div>
                <div className="mt-0.5 text-sm font-medium text-ink-800 dark:text-ink-100">
                  {row.provider}
                </div>
                <div className="text-2xs text-ink-400">
                  {row.chosen_by === 'account'
                    ? 'set here'
                    : "not set here — the bot's or the platform default"}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card title="Language model" description="What the bot thinks with.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Provider" hint="Blank uses the platform default.">
            <Select
              value={draft.llm_provider_id ?? ''}
              onChange={(e) =>
                set({ llm_provider_id: e.target.value, llm_model: '' })
              }
            >
              <option value="">Platform default</option>
              {llmProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Model"
            hint={llmModels.find((m) => m.id === draft.llm_model)?.note}
          >
            <Select
              value={draft.llm_model ?? ''}
              onChange={(e) => set({ llm_model: e.target.value })}
            >
              <option value="">
                Inherit ({settings.data?.effective.llm_model})
              </option>
              {llmModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="mt-2 text-xs text-ink-500">
          A reasoning-grade model on a live call is seconds of silence per turn.
          Pick a fast one unless the flow genuinely needs the other.
        </p>
      </Card>

      <Card title="Speech to text" description="What the bot hears with.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Provider" hint="Blank uses the platform default.">
            <Select
              value={draft.stt_provider_id ?? ''}
              onChange={(e) => set({ stt_provider_id: e.target.value, stt_model: '' })}
            >
              <option value="">Platform default</option>
              {sttProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Model"
            hint={sttModels.find((m) => m.id === draft.stt_model)?.note}
          >
            <Select
              value={draft.stt_model ?? ''}
              onChange={(e) => set({ stt_model: e.target.value })}
            >
              <option value="">
                Inherit ({settings.data?.effective.stt_model})
              </option>
              {sttModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      <Card title="Text to speech" description="What the bot speaks with.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Provider">
            <Select
              value={draft.tts_provider_id ?? ''}
              onChange={(e) =>
                set({ tts_provider_id: e.target.value, tts_model: '' })
              }
            >
              <option value="">Platform default</option>
              {ttsProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Model"
            hint={ttsModels.find((m) => m.id === draft.tts_model)?.note}
          >
            {ttsModels.length ? (
              <Select
                value={draft.tts_model ?? ''}
                onChange={(e) => set({ tts_model: e.target.value })}
              >
                <option value="">
                  Inherit ({settings.data?.effective.tts_model})
                </option>
                {ttsModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                value={draft.tts_model ?? ''}
                placeholder="Inherit"
                onChange={(e) => set({ tts_model: e.target.value })}
              />
            )}
          </Field>
        </div>

        <div className="mt-3" />
        <Field
          label="Voice ID"
          hint="Blank inherits: the bot's own voice first, then the platform default. Paste the ID from the provider — nothing here is hard-coded."
        >
          <Input
            value={draft.tts_voice_id ?? ''}
            placeholder={settings.data?.effective.tts_voice_id || 'Inherit'}
            className="font-mono text-xs"
            onChange={(e) => set({ tts_voice_id: e.target.value.trim() })}
          />
        </Field>

        {!!voiceFields.length && (
          <div className="mt-4 border-t border-line pt-3">
            <p className="text-xs text-ink-500">
              Voice settings. Left blank, the provider&apos;s own defaults apply.
              Changing any of these produces new audio rather than reusing what
              is cached.
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {voiceFields.map((field) => (
                <Field key={field.key} label={field.label} hint={field.hint}>
                  <Input
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={draft.tts_settings?.[field.key] ?? ''}
                    placeholder="provider default"
                    onChange={(e) =>
                      set({
                        tts_settings: {
                          ...(draft.tts_settings ?? {}),
                          [field.key]:
                            e.target.value === '' ? undefined : Number(e.target.value),
                        },
                      })
                    }
                  />
                </Field>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Next to the voice field, not on a page of its own: the ID box above
          is where somebody is standing when they realise they cannot tell one
          twenty-character ID from another. */}
      <VoiceLibrary tenantId={tenantId} />

      <Card
        title="Voice cache"
        description="The same line, said once and kept."
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setClearing(true)}
            disabled={!settings.data?.cache?.entries}
          >
            Clear this client&apos;s cache
          </Button>
        }
      >
        <Toggle
          label="Keep this client's audio"
          hint="Off only for an account whose every line is unique, where the cache is buying disk and nothing else."
          checked={draft.voice_cache_enabled ?? true}
          onChange={(v) => set({ voice_cache_enabled: v })}
        />
        {settings.data?.cache && (
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-500">
            <span>
              Lines cached{' '}
              <strong className="tabular-nums text-ink-800 dark:text-ink-100">
                {settings.data.cache.entries}
              </strong>
            </span>
            <span>
              On disk{' '}
              <strong className="tabular-nums text-ink-800 dark:text-ink-100">
                {bytes(settings.data.cache.bytes)}
              </strong>
            </span>
            <span>
              Vendor calls avoided{' '}
              <strong className="tabular-nums text-ink-800 dark:text-ink-100">
                {settings.data.cache.hits}
              </strong>
            </span>
          </div>
        )}
        <p className="mt-3 text-2xs text-ink-500">
          Cached audio is keyed by client, provider, voice, model, speed, sample
          rate, voice settings and the exact text. Two clients on different
          voices can never be served each other&apos;s audio, and changing the
          voice or the model produces new audio rather than reusing the old.
        </p>
      </Card>

      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-500">
          In use now:{' '}
          <Badge tone="gray">{settings.data?.effective.stt_model}</Badge>{' '}
          <Badge tone="gray">{settings.data?.effective.tts_model}</Badge>{' '}
          {settings.data?.effective.tts_voice_id && (
            <Badge tone="gray">{settings.data.effective.tts_voice_id}</Badge>
          )}
        </div>
        <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save AI settings'}
        </Button>
      </div>

      <ConfirmDialog
        open={clearing}
        title="Clear this client's voice cache?"
        confirmLabel="Clear it"
        busy={clearCache.isPending}
        onCancel={() => setClearing(false)}
        onConfirm={() => clearCache.mutate()}
        body={
          <>
            <p>
              Removes {settings.data?.cache?.entries ?? 0} cached lines for this
              client only. Other clients are untouched.
            </p>
            <p>
              Nothing is lost permanently — the next call that needs a line buys
              it again. Reach for this after changing the voice or the model, if
              you want the old audio gone immediately rather than simply unused.
            </p>
          </>
        }
      />
    </div>
  );
}
